<script lang="ts">
import { inject } from 'vue'
import type Map from 'ol/Map.js'
import type VectorSource from 'ol/source/Vector'
import Snap from 'ol/interaction/Snap'
import { Modify, Select } from 'ol/interaction'
import DrawHole from 'ol-ext/interaction/DrawHole'
import type VectorLayer from 'ol/layer/Vector'
import type { SelectEvent } from 'ol/interaction/Select'
import type { DrawEvent } from 'ol/interaction/Draw'
import Draw from 'ol/interaction/Draw'
import type { FeatureLike } from 'ol/Feature'
import type { SimpleGeometry } from 'ol/geom'
import { MultiLineString, MultiPoint, MultiPolygon } from 'ol/geom'
import type { Type as GeometryType } from 'ol/geom/Geometry'
import type { Polygon } from 'geojson'

export default {
    name: 'DkOlInteractionModify',
    components: {},
    props: {
        create: {
            type: Boolean,
            required: false,
            default: false,
        },
        createType: {
            // type: GeometryType,
            type: String,
            required: true,
        },
        enableSnap: {
            type: Boolean,
            required: false,
            default: true,
        },
        snapSource: {
            required: false,
            default: null,
        },
        interaction: {
            type: String,
            required: false,
            default: null,
        },
        allowMultipleGeometry: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['updateGeometry'],
    data() {
        return {
            map: null as Map,
            vectorSource: null as VectorSource | null,
            vectorLayer: null as VectorLayer | null,
            snap: null as Snap,
            selfSnap: null as Snap,
            isCreation: false as boolean,
            modify: null as Modify,
            draw: null as Draw,
            drawHole: null as DrawHole,
            delete: null as Select,
        }
    },
    computed: {},
    watch: {
        enableSnap() {
            this.initSnap(this.enableSnap)
        },
        interaction() {
            this.switchInteraction()
            this.initSnap(this.enableSnap)
        },
    },
    unmounted(): void {
        this.resetInteractions()
    },
    mounted() {
        this.map = inject('map')
        this.vectorSource = inject('vectorSource')
        this.vectorLayer = inject('vectorLayer')
        this.isCreation = !this.vectorSource?.getFeatures().length > 0

        if (this.isCreation) {
            this.initDrawInteraction()
        }
        else {
            this.initModifyInteraction()
        }

        this.initSnap(this.enableSnap)
    },
    methods: {
        initSnap(enable: boolean = true): void {
            // snap ex: https://openlayers.org/en/latest/examples/snap.html
            if (!enable) {
                if (this.snap) {
                    this.map.removeInteraction(this.snap)
                }
                if (this.selfSnap) {
                    this.map.removeInteraction(this.selfSnap)
                }
                this.snap = null
                this.selfSnap = null
                return
            }
            const snapSource = this.vectorSource
            if (snapSource) {
                this.selfSnap = new Snap({
                    source: snapSource,
                    intersection: true,
                })
                this.map.addInteraction(this.selfSnap)
            }
            if (this.snapSource) {
                this.snap = new Snap({
                    source: this.snapSource as VectorSource,
                    intersection: true,
                })
                this.map.addInteraction(this.snap)
            }
        },
        resetInteractions(exceptSnap = false): void {
            const interactions = [this.modify, this.drawHole, this.draw, this.delete]
            if (!exceptSnap) {
                interactions.push(this.snap)
                interactions.push(this.selfSnap)
            }
            // remove interactions
            for (const interaction of interactions) {
                if (interaction) {
                    interaction.setActive(false)
                    this.map.removeInteraction(interaction)
                }
            }
        },
        switchInteraction(): void {
            this.resetInteractions()
            switch (this.interaction) {
                case 'DrawHole':
                    return this.initDrawHoleInteraction()
                case 'Draw':
                    return this.initDrawInteraction()
                case 'Modify':
                    return this.initModifyInteraction()
                case 'Delete':
                    return this.initDeleteInteraction()
            }
        },
        initDrawInteraction(): void {
            const interaction = new Draw({
                type: this.createType,
                source: this.vectorSource,
            })
            interaction.on('drawend', (e: DrawEvent) => {
                const features = this.vectorSource.getFeatures()
                features.push(e.feature)
                this.emitFeatureUpdate(features)
            })
            this.map.addInteraction(interaction)
            this.draw = interaction
        },
        initModifyInteraction(): void {
            const interaction = new Modify({
                source: this.vectorSource,
            })
            interaction.on('modifyend', () => {
                this.emitFeatureUpdate()
            })
            this.map.addInteraction(interaction)
            this.modify = interaction
        },
        initDeleteInteraction(): void {
            const interaction = new Select({
                hitTolerance: 2,
                layers: [this.vectorLayer],
            })
            interaction.on('select', (e: SelectEvent) => {
                this.vectorSource.removeFeatures(e.selected)
                this.emitFeatureUpdate()
            })
            this.map.addInteraction(interaction)
            this.draw = interaction
        },
        initDrawHoleInteraction(): void {
            const interaction = new DrawHole({
                layers: [this.vectorLayer],
            })
            interaction.on('modifyend', () => {
                this.emitFeatureUpdate()
            })
            this.map.addInteraction(interaction)
            this.drawHole = interaction
        },

        emitFeatureUpdate(features: FeatureLike[] | null = null): void {
            if (!features) {
                features = this.vectorSource.getFeatures() as FeatureLike[]
            }
            if (features.length === 0) {
                this.$emit('updateGeometry', null)
                return
            }
            if (features.length === 1) {
                this.$emit('updateGeometry', (features[0] as FeatureLike).getGeometry())
                return
            }
            // merge features as MultiGeom
            // create new features
            const layout = (features[0].getGeometry() as SimpleGeometry).getLayout()
            let mergeGeometry: SimpleGeometry
            switch (this.createType as GeometryType) {
                case 'MultiPoint':
                case 'Point':
                    mergeGeometry = new MultiPoint([], layout)
                    break
                case 'MultiLineString':
                case 'LineString':
                    mergeGeometry = new MultiLineString([], layout)
                    break
                case 'MultiPolygon':
                case 'Polygon':
                    mergeGeometry = new MultiPolygon([], layout)
                    break
            }

            // merge features
            features.forEach((feature: FeatureLike) => {
                const geometry: SimpleGeometry = feature.getGeometry()
                switch (geometry.getType() as GeometryType) {
                    case 'MultiPoint':
                        (geometry as MultiPoint).getPoints().forEach((point: Point) => {
                            (mergeGeometry as MultiPoint).appendPoint(point)
                        })
                        break
                    case 'Point':
                        (mergeGeometry as MultiPoint).appendPoint(geometry)
                        break
                    case 'MultiLineString':
                        (geometry as MultiLineString).getLineStrings().forEach((lineString: LineString) => {
                            (mergeGeometry as MultiLineString).appendLineString(lineString)
                        })
                        break
                    case 'LineString':
                        (mergeGeometry as MultiLineString).appendLineString(geometry)
                        break
                    case 'MultiPolygon':
                        (geometry as MultiPolygon).getPolygons().forEach((polygon: Polygon) => {
                            (mergeGeometry as MultiPolygon).appendPolygon(polygon)
                        })
                        break
                    case 'Polygon':
                        (mergeGeometry as MultiPolygon).appendPolygon(geometry)
                        break
                }
            })

            this.$emit('updateGeometry', mergeGeometry)
        },

    },
}
</script>

<template>
    <slot/>
</template>

<style>

</style>
